import { Logger } from '@unidev-hub/logger';
import { ConfigManager } from '@unidev-hub/config';
import cors from 'cors';
import helmet from 'helmet';
import compression from 'compression';
import bodyParser from 'body-parser';

import {
  correlationIdMiddleware,
  requestLoggerMiddleware,
  responseFormatterMiddleware,
  rateLimiterMiddleware,
  metricsMiddleware,
  errorHandlerMiddleware
} from './index';

import {
  registerMiddleware,
  RegularMiddlewareFactory,
  ErrorMiddlewareFactory
} from './registry';

import { Middleware } from './types';

// Security middleware - Helmet
registerMiddleware(Middleware.HELMET, {
  create: (config: ConfigManager, logger: Logger) => {
    logger.debug('Initializing helmet middleware');
    return helmet(config.get('api.security.helmetOptions', {}));
  },
  order: 10,
  isErrorHandler: false
} as RegularMiddlewareFactory);

// Security middleware - CORS
registerMiddleware(Middleware.CORS, {
  create: (config: ConfigManager, logger: Logger) => {
    logger.debug('Initializing CORS middleware');
    const corsOptions = config.get('api.security.corsOptions', {});
    return cors(corsOptions);
  },
  order: 20,
  isErrorHandler: false
} as RegularMiddlewareFactory);

// Compression middleware
registerMiddleware(Middleware.COMPRESSION, {
  create: (config: ConfigManager, logger: Logger) => {
    logger.debug('Initializing compression middleware');
    return compression();
  },
  order: 30,
  isErrorHandler: false
} as RegularMiddlewareFactory);

// Body parser middleware
registerMiddleware(Middleware.BODY_PARSER, {
  create: (config: ConfigManager, logger: Logger) => {
    logger.debug('Initializing body parser middleware');
    const bodyLimit = config.get('api.bodyLimit', '1mb');
    return [
      bodyParser.json({ limit: bodyLimit }),
      bodyParser.urlencoded({ extended: true, limit: bodyLimit })
    ];
  },
  order: 40,
  isErrorHandler: false
} as RegularMiddlewareFactory);

// Correlation ID middleware
registerMiddleware(Middleware.CORRELATION, {
  create: (config: ConfigManager, logger: Logger) => {
    logger.debug('Initializing correlation ID middleware');
    const correlationConfig = config.get('api.correlationId', {});
    return correlationIdMiddleware(correlationConfig);
  },
  order: 50,
  isErrorHandler: false
} as RegularMiddlewareFactory);

// Request logging middleware
registerMiddleware(Middleware.LOGGING, {
  create: (config: ConfigManager, logger: Logger) => {
    logger.debug('Initializing request logging middleware');
    return requestLoggerMiddleware(logger, config.get('api.logging', {}));
  },
  dependencies: [Middleware.CORRELATION],
  order: 60,
  isErrorHandler: false
} as RegularMiddlewareFactory);

// Response formatter middleware
registerMiddleware(Middleware.RESPONSE_FORMAT, {
  create: (config: ConfigManager, logger: Logger) => {
    logger.debug('Initializing response formatter middleware');
    const formatConfig = config.get('api.responseFormat', {});
    return responseFormatterMiddleware(formatConfig);
  },
  dependencies: [Middleware.CORRELATION],
  order: 70,
  isErrorHandler: false
} as RegularMiddlewareFactory);

// Rate limiting middleware
registerMiddleware(Middleware.RATE_LIMIT, {
  create: (config: ConfigManager, logger: Logger) => {
    logger.debug('Initializing rate limit middleware');
    const rateLimitConfig = config.get('api.rateLimit', {});
    return rateLimiterMiddleware(rateLimitConfig);
  },
  order: 80,
  isErrorHandler: false
} as RegularMiddlewareFactory);

// Metrics middleware
registerMiddleware(Middleware.METRICS, {
  create: (config: ConfigManager, logger: Logger) => {
    logger.debug('Initializing metrics middleware');
    const metricsConfig = config.get('api.metrics', {});
    // Safe check that the returned object has middleware property
    const result = metricsMiddleware(metricsConfig);
    if (result && typeof result === 'object' && 'middleware' in result) {
      return result.middleware;
    }
    return (req: any, res: any, next: any) => next();
  },
  order: 90,
  isErrorHandler: false
} as RegularMiddlewareFactory);

// Error handling middleware
registerMiddleware(Middleware.ERROR_HANDLING, {
  create: (config: ConfigManager, logger: Logger) => {
    logger.debug('Initializing error handling middleware');
    const errorConfig = config.get('api.errorHandling', {});
    return errorHandlerMiddleware(errorConfig, logger);
  },
  order: 1000,
  isErrorHandler: true
} as ErrorMiddlewareFactory);
